import type { ApiClient } from '../../../shared/api/api-client';

export type PaymentTotalsDto = {
  cash_total: number;
  bank_total: number;
};

export type PaymentAuditItemDto = {
  id?: number;
  created_at_utc?: string;
  gateway?: string;
  direction?: string;
  transaction_id?: string;
  branch_id?: number | null;
  client_display_name?: string | null;
  status?: string | null;
};

export type PlatformModuleItemDto = {
  module_code: string;
  is_enabled: boolean;
  is_core: boolean;
};

export type GlobalGatewaysDto = {
  active_payment_gateway?: string | null;
  robokassa_enabled?: boolean;
  yookassa_enabled?: boolean;
  tochka_enabled?: boolean;
};

export type AuditEntryDto = {
  created_at: string;
  action: string;
};

export type DashboardExtendedDto = {
  orgCount: number;
  userTotal: number;
  gw: GlobalGatewaysDto | null;
  payTotals: PaymentTotalsDto | null;
  monthCurrentTotal: number;
  monthDeltaPct: number | null;
  disabledModules: number;
  paymentFailures: number;
  paymentPending: number;
  weeklySeries: Array<{ label: string; cash: number; bank: number; total: number }>;
  auditDaily: Array<{ label: string; events: number }>;
  orgWeeklyGrowth: Array<{ label: string; count: number }>;
  userWeeklyGrowth: Array<{ label: string; count: number }>;
};

type QueryParams = Record<string, string | number | undefined>;

function lastNWeekIsoRanges(n: number): Array<{ created_from: string; created_to: string; label: string }> {
  const now = new Date();
  const out: Array<{ created_from: string; created_to: string; label: string }> = [];
  for (let w = n - 1; w >= 0; w--) {
    const weekEnd = new Date(now.getTime() - w * 7 * 24 * 60 * 60 * 1000);
    const weekStart = new Date(weekEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
    const dd = String(weekStart.getUTCDate()).padStart(2, '0');
    const mm = String(weekStart.getUTCMonth() + 1).padStart(2, '0');
    out.push({
      created_from: weekStart.toISOString(),
      created_to: weekEnd.toISOString(),
      label: `${dd}.${mm}`
    });
  }
  return out;
}

function last30dRange(): { created_from: string; created_to: string } {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - 30);
  return { created_from: start.toISOString(), created_to: end.toISOString() };
}

function monthRangeUTC(offsetFromCurrent: number): { created_from: string; created_to: string } {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offsetFromCurrent, 1, 0, 0, 0));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offsetFromCurrent + 1, 1, 0, 0, 0));
  return { created_from: start.toISOString(), created_to: end.toISOString() };
}

const SUPERADMIN_AUDIT_LOG_MAX = 500;

export async function fetchDashboardExtended(apiClient: ApiClient): Promise<DashboardExtendedDto> {
  const period = last30dRange();
  const monthCurrent = monthRangeUTC(0);
  const monthPrev = monthRangeUTC(-1);
  const weekRanges = lastNWeekIsoRanges(8);
  const auditFrom = new Date();
  auditFrom.setUTCDate(auditFrom.getUTCDate() - 14);

  const qs = (params: QueryParams): string => {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '';
    return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
  };

  const [
    gwRaw,
    payTotalsRaw,
    payCurrentMonthRaw,
    payPrevMonthRaw,
    modulesRaw,
    pgaListRaw
  ] = await Promise.all([
    apiClient.request<unknown>('/superadmin/global-payment-gateways', { method: 'GET' }) as Promise<GlobalGatewaysDto>,
    apiClient.request<unknown>(
      `/superadmin/payment-gateway-audit/totals${qs({ created_from: period.created_from, created_to: period.created_to })}`,
      { method: 'GET' }
    ) as Promise<PaymentTotalsDto>,
    apiClient.request<unknown>(
      `/superadmin/payment-gateway-audit/totals${qs({ created_from: monthCurrent.created_from, created_to: monthCurrent.created_to })}`,
      { method: 'GET' }
    ) as Promise<PaymentTotalsDto>,
    apiClient.request<unknown>(
      `/superadmin/payment-gateway-audit/totals${qs({ created_from: monthPrev.created_from, created_to: monthPrev.created_to })}`,
      { method: 'GET' }
    ) as Promise<PaymentTotalsDto>,
    apiClient.request<unknown>('/superadmin/modules', { method: 'GET' }) as Promise<{ items: PlatformModuleItemDto[] }>,
    apiClient.request<unknown>(
      `/superadmin/payment-gateway-audit${qs({ limit: 300, offset: 0, created_from: period.created_from, created_to: period.created_to })}`,
      { method: 'GET' }
    ) as Promise<{ items: PaymentAuditItemDto[] }>
  ]);

  // Weekly payment series
  const weeklySettled = await Promise.allSettled(
    weekRanges.map((r) =>
      apiClient.request<unknown>(
        `/superadmin/payment-gateway-audit/totals${qs({ created_from: r.created_from, created_to: r.created_to })}`,
        { method: 'GET' }
      )
    )
  );
  const weeklySeries = weekRanges.map((r, i) => {
    const settled = weeklySettled[i];
    if (settled?.status === 'fulfilled' && settled.value) {
      const t = settled.value as PaymentTotalsDto;
      return {
        label: r.label,
        cash: t.cash_total ?? 0,
        bank: t.bank_total ?? 0,
        total: (t.cash_total ?? 0) + (t.bank_total ?? 0)
      };
    }
    return { label: r.label, cash: 0, bank: 0, total: 0 };
  });

  // Audit growth
  const growthFromIso = weekRanges[0]?.created_from ?? new Date(0).toISOString();
  const [orgGrowthRes, userCreateRes, registerRes] = await Promise.allSettled([
    apiClient.request<unknown>(
      `/superadmin/audit-log${qs({ limit: SUPERADMIN_AUDIT_LOG_MAX, offset: 0, action: 'organization_create', created_from: growthFromIso })}`,
      { method: 'GET' }
    ),
    apiClient.request<unknown>(
      `/superadmin/audit-log${qs({ limit: SUPERADMIN_AUDIT_LOG_MAX, offset: 0, action: 'user_create', created_from: growthFromIso })}`,
      { method: 'GET' }
    ),
    apiClient.request<unknown>(
      `/superadmin/audit-log${qs({ limit: SUPERADMIN_AUDIT_LOG_MAX, offset: 0, action: 'register', created_from: growthFromIso })}`,
      { method: 'GET' }
    )
  ]);

  const orgCreates = (orgGrowthRes.status === 'fulfilled' ? orgGrowthRes.value : { items: [] }) as { items: AuditEntryDto[] };
  const userCreates = (userCreateRes.status === 'fulfilled' ? userCreateRes.value : { items: [] }) as { items: AuditEntryDto[] };
  const registers = (registerRes.status === 'fulfilled' ? registerRes.value : { items: [] }) as { items: AuditEntryDto[] };
  const userGrowthEntries = [...(userCreates.items ?? []), ...(registers.items ?? [])];

  const bucketByWeek = (entries: AuditEntryDto[], ranges: typeof weekRanges): Array<{ label: string; count: number }> => {
    return ranges.map((r) => {
      const fromMs = new Date(r.created_from).getTime();
      const toMs = new Date(r.created_to).getTime();
      const count = entries.filter((e) => {
        const t = new Date(e.created_at).getTime();
        return t >= fromMs && t <= toMs;
      }).length;
      return { label: r.label, count };
    });
  };

  const orgWeeklyGrowth = bucketByWeek(orgCreates.items ?? [], weekRanges);
  const userWeeklyGrowth = bucketByWeek(userGrowthEntries, weekRanges);

  // Daily audit
  let auditLarge: AuditEntryDto[] = [];
  try {
    const raw = await apiClient.request<unknown>(
      `/superadmin/audit-log${qs({ limit: SUPERADMIN_AUDIT_LOG_MAX, offset: 0, created_from: auditFrom.toISOString() })}`,
      { method: 'GET' }
    );
    auditLarge = (raw as { items: AuditEntryDto[] }).items ?? [];
  } catch {
    auditLarge = [];
  }
  const dayKeys: string[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i);
    dayKeys.push(d.toISOString().slice(0, 10));
  }
  const dayCount = new Map<string, number>();
  for (const row of auditLarge) {
    const k = row.created_at.slice(0, 10);
    dayCount.set(k, (dayCount.get(k) ?? 0) + 1);
  }
  const auditDaily = dayKeys.map((k) => ({
    label: `${k.slice(8, 10)}.${k.slice(5, 7)}`,
    events: dayCount.get(k) ?? 0
  }));

  const monthCurrentTotal = (payCurrentMonthRaw?.cash_total ?? 0) + (payCurrentMonthRaw?.bank_total ?? 0);
  const monthPrevTotal = (payPrevMonthRaw?.cash_total ?? 0) + (payPrevMonthRaw?.bank_total ?? 0);
  const monthDeltaPct = monthPrevTotal > 0 ? ((monthCurrentTotal - monthPrevTotal) / monthPrevTotal) * 100 : null;

  const moduleItems = (modulesRaw as { items?: PlatformModuleItemDto[] })?.items ?? [];
  const disabledModules = moduleItems.filter((m) => !m.is_enabled).length;

  const pgaList = (pgaListRaw as { items?: PaymentAuditItemDto[] })?.items ?? [];
  const paymentFailures = pgaList.filter((row) => {
    const st = (row.status ?? '').toLowerCase();
    return st === 'error' || st === 'failed' || st === 'fail';
  }).length;
  const paymentPending = pgaList.filter((row) => (row.status ?? '').toLowerCase() === 'pending').length;

  return {
    orgCount: 0, // filled from main dashboard
    userTotal: 0, // filled from main dashboard
    gw: gwRaw as GlobalGatewaysDto | null,
    payTotals: payTotalsRaw as PaymentTotalsDto | null,
    monthCurrentTotal,
    monthDeltaPct,
    disabledModules,
    paymentFailures,
    paymentPending,
    weeklySeries,
    auditDaily,
    orgWeeklyGrowth,
    userWeeklyGrowth
  };
}
